import React, { useMemo } from 'react';
import { useParams } from 'react-router';
import { Field, useField, useFormState } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import useActiv8TagsOptions from '../../hooks/useActiv8TagsOptions';
import MultiselectDropdown from '../../../components/multiselect-dropdown/MultiselectDropdown';
import { useProject } from '../../../context/ProjectContext';
import { addLocalTag } from '../../../redux/activ8/activ8Reducer';
import WithLabel, { LABEL_STYLES } from '../../../components/with-label/WithLabel';
import CommonButton, { BUTTON_STYLES } from '../../../components/common-button/CommonButton';

import scss from './EditTagsModal.module.scss';

function EditTagsFormInner({ onSubmit, onCancel }) {
  const { t } = useTranslation('activ8');
  const dispatch = useDispatch();

  const { actions } = useProject();
  const { id: projectId } = useParams();
  const { submitting } = useFormState();

  const allTagsOptions = useActiv8TagsOptions();
  const isTagsLoading = useSelector((state) => state.activ8.isTagsLoading);

  const { input } = useField('tags');
  const selectedTagsOptions = useMemo(() => {
    const tags = Array.isArray(input.value) ? input.value : [];

    return tags.map((tag) => allTagsOptions.find((option) => option.value === tag) || { value: tag, label: tag });
  }, [input.value, allTagsOptions]);

  const handleUpdateTags = (tags) => {
    input.onChange(tags.map((tag) => tag.value));
  };

  const handleAddNewTag = (tag) => {
    dispatch(addLocalTag(tag.value));
  };

  const handleSearchTags = (value) => {
    actions('searchTags', { id: projectId, query: value });
  };

  return (
    <form onSubmit={onSubmit} className={scss.form}>
      <h3 className={scss.title}>{t('editTags')}</h3>

      <Field name='tags' options={allTagsOptions}>
        {() => (
          <WithLabel label={t('tags')} variant={LABEL_STYLES.COLUMN} isBold isTwoDots>
            <MultiselectDropdown
              value={selectedTagsOptions}
              options={allTagsOptions}
              onChange={handleUpdateTags}
              onSearch={handleSearchTags}
              isInitialSearch={false}
              onAddNewOption={handleAddNewTag}
              isLoading={isTagsLoading}
              isDebounce
              placeholder={t('tags')}
              usePortal
            />
          </WithLabel>
        )}
      </Field>

      <div className={scss.actions}>
        <CommonButton
          type='button'
          styleColor={BUTTON_STYLES.SECONDARY}
          maxWidth={144}
          onClick={onCancel}
          disabled={submitting}>
          {t('cancel')}
        </CommonButton>

        <CommonButton type='submit' styleColor={BUTTON_STYLES.PRIMARY} maxWidth={144} isLoading={submitting}>
          {t('submit')}
        </CommonButton>
      </div>
    </form>
  );
}

export default EditTagsFormInner;
